import {
  createSlice,
  isPending,
  isRejected,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';

import {
  cancelCourseTeamsSectionAsAdmin,
  cancelCoursZoomeSectionAsAdmin,
  createCourseHomeworkSectionAsAdmin,
  createNewCourseResourceSectionAsAdmin,
  createNewCourseResourceSectionAsAdminForTemplate,
  createNewCourseSectionAsAdmin,
  createNewCourseSectionAsAdminFromTemplate,
  createNewCourseTeamSectionAsAdmin,
  createNewCourseTeamsSectionAsAdminFromTemplate,
  createNewCourseZoomSectionAsAdmin,
  createNewCourseZoomSectionAsAdminFromTemplate,
  deleteCourseResourceSectionByIdAsAdmin,
  deleteCourseSectionByIdAsAdmin,
  deleteCourseTeamSectionByIdAsAdmin,
  deleteCourseZoomSectionByIdAsAdmin,
  getAdminCourseSectionsByCourseId,
  getSectionByIdAsAdmin,
  rescheduleCourseTeamsSectionAsAdmin,
  rescheduleCoursZoomeSectionAsAdmin,
  updateCourseHomeworkSectionAsAdmin,
  updateCourseResourceSectionAsAdmin,
  updateCourseTeamsSectionAsAdmin,
  updateCourseZoomSectionAsAdmin,
} from './adminApi';
import { RootState } from '../..';
import { updateCourseSectionAsAdminSavingSlice } from '../SavingSlice';
import {
  bookCourseMeeting,
  cancelCourseTeamsSectionAsUser,
  cancelCoursZoomeSectionAsUser,
  createCourseHomeworkSectionAsUser,
  getSectionByIdAsUser,
  getUserCourseSectionsByCourseId,
  rescheduleCourseTeamsSectionAsUser,
  rescheduleCoursZoomeSectionAsUser,
} from './api';
import { IMeetingRequest } from '../MeetingRequestsSlice';
import { ILessonHistory } from '../LessonHistorySlice';
import { IUser } from '../../UserSlice';
import { ICourse } from '../CoursesSlice';

export const SECTION_TYPES = {
  SECTION: 'SECTION',
  ZOOM: 'ZOOM',
  RESOURCE: 'RESOURCE',
  HOMEWORK: 'HOMEWORK',
  TEAMS: 'TEAMS',
} as const;

export const SECTION_STATUSES = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
} as const;

export const ON_BEHALF_USER_ROLES = {
  STUDENT: 'STUDENT',
  TUTOR: 'TUTOR',
} as const;

export const LESSON_TYPES = {
  TRIAL: 'TRIAL',
  DUMMY: 'DUMMY',
  LESSON: 'LESSON',
} as const;

export const MEETING_SECTION_PAYMENT_TYPES = {
  BILLABLE: 'BILLABLE',
  NON_BILLABLE: 'NON_BILLABLE',
} as const;

export type TSectionTypes = typeof SECTION_TYPES[keyof typeof SECTION_TYPES];
export type TSectionStatus = typeof SECTION_STATUSES[keyof typeof SECTION_STATUSES];
export type TOnBehalfOfUserRole = typeof ON_BEHALF_USER_ROLES[keyof typeof ON_BEHALF_USER_ROLES];
export type TLessonTypes = typeof LESSON_TYPES[keyof typeof LESSON_TYPES];
export type TMeetingSectionPaymentType = typeof MEETING_SECTION_PAYMENT_TYPES[keyof typeof MEETING_SECTION_PAYMENT_TYPES];

export type TSectionZoomRecording = {
  id: string;
  url: string;
  type: 'MP4' | 'M4A' | 'CHAT' | 'TRANSCRIPT';
  size: number;
};

export interface ISection {
  _id: string;
  course: string | ICourse;
  type: TSectionTypes;
  title: string;
  description: string;
  createdAt: string;
  updateAt: string;
  sortingKey: number;
  status: TSectionStatus;
  parent?: string;
  sections?: Array<ISection>;
  template?: string;
  startDate?: string;
  endDate?: string;
  // ZOOM
  dateTime?: string;
  duration?: number;
  tutorWaitingId?: string;
  studentWaitingId?: string;
  adminWaitingId?: string;
  zoomRecordings?: TSectionZoomRecording[];
  tutorJoinTime?: number;
  studentJoinTime?: number;
  zoomRequest?: IMeetingRequest;
  eventId?: string;
  classWaitingId?: string;
  lessonHistory?: string | ILessonHistory;
  tutor?: string | IUser;
  lessonType?: TLessonTypes;
  paymentType?: TMeetingSectionPaymentType;
  // RESOURCE
  curriculum?: string;
  grade?: string;
  subject?: string;
  subjectCode?: string;
  subjectLevel?: string;
  quiztopiyaTopicId?: string;
  quiztopiyaSubTopicId?: string;
  quiztopiyaTopicName?: string;
  quiztopiyaSubTopicName?: string;
  quiztopiyaResourceUrl?: string;
  // HOMEWORK
  dueDate?: string;
}

export interface IInitialSectionsState {
  status: 'idle' | 'loading' | 'rejected';
  newlyAddedSectionId?: string;
  userCourseSections: Array<ISection>;
  userSection?: ISection;
  // Admins
  adminCourseSections: Array<ISection>;
  adminSection?: ISection;
}

const initialState: IInitialSectionsState = {
  status: 'idle',
  newlyAddedSectionId: undefined,
  userCourseSections: [],
  userSection: undefined,
  // Admins
  adminCourseSections: [],
  adminSection: undefined,
};

const modifySectionListOnCreate = (
  tree: ISection[],
  id: string,
  newData: ISection
) => {
  let index = 0;

  if (!Array.isArray(tree)) return false;

  // eslint-disable-next-line no-restricted-syntax
  for (const node of tree) {
    if (node._id === id) {
      let _sections = tree[index].sections;

      if (_sections && _sections.length) {
        _sections.push(newData);
        _sections.sort((a, b) => a.sortingKey - b.sortingKey);
      } else {
        _sections = [newData];
      }

      tree[index].sections = _sections;

      return true;
    }

    if (node.sections && node.sections.length > 0) {
      if (modifySectionListOnCreate(node.sections, id, newData)) {
        return true;
      }
    }

    index += 1;
  }

  return false;
};

const modifySectionListOnUpdate = (
  tree: ISection[],
  id: string,
  newData: ISection
) => {
  let index = 0;

  if (!Array.isArray(tree)) return false;

  // eslint-disable-next-line no-restricted-syntax
  for (const node of tree) {
    if (node._id === id) {
      tree[index] = {
        ...newData,
        sections: tree[index].sections || [],
      };
      return true;
    }

    if (node.sections && node.sections.length > 0) {
      if (modifySectionListOnUpdate(node.sections, id, newData)) {
        return true;
      }
    }

    index += 1;
  }

  return false;
};

const modifySectionListOnDeleteItem = (
  tree: ISection[],
  id: string
): boolean => {
  let index = 0;

  if (!Array.isArray(tree)) return false;

  // eslint-disable-next-line no-restricted-syntax
  for (const node of tree) {
    if (node._id === id) {
      tree.splice(index, 1);
      return true;
    }

    if (node.sections && node.sections.length > 0) {
      if (modifySectionListOnDeleteItem(node.sections, id)) {
        return true;
      }
    }

    index += 1;
  }

  return false;
};

export const SectionsSlice = createSlice({
  name: 'sections',
  initialState,
  reducers: {
    resetUserCourseSections: (state) => {
      state.userCourseSections = [];
    },
    updateCourseSectionState: (
      state,
      action: PayloadAction<{ id: string; section: ISection }>
    ) => {
      const newSections: Array<ISection> = JSON.parse(
        JSON.stringify([...state.userCourseSections])
      );

      const idToModify = action.payload.id;
      const dataToChange = action.payload.section;

      const success = modifySectionListOnUpdate(
        newSections,
        idToModify,
        dataToChange
      );

      if (success) {
        state.userCourseSections = newSections;
      }
    },
    resetUserSection: (state) => {
      state.userSection = undefined;
    },
    // Admin
    resetAdminCourseSections: (state) => {
      state.adminCourseSections = [];
    },
    resetNewlyAddedSectionId: (state) => {
      state.newlyAddedSectionId = undefined;
    },
    resetAdminSection: (state) => {
      state.adminSection = undefined;
    },
    updateAdminCourseSectionState: (
      state,
      action: PayloadAction<{ id: string; section: ISection }>
    ) => {
      const newSections: Array<ISection> = JSON.parse(
        JSON.stringify([...state.adminCourseSections])
      );

      const idToModify = action.payload.id;
      const dataToChange = action.payload.section;

      const success = modifySectionListOnUpdate(
        newSections,
        idToModify,
        dataToChange
      );

      if (success) {
        state.adminCourseSections = newSections;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cancelCoursZoomeSectionAsUser.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(cancelCourseTeamsSectionAsUser.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(rescheduleCoursZoomeSectionAsUser.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(rescheduleCourseTeamsSectionAsUser.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(getSectionByIdAsUser.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.userSection = result;
      })
      .addCase(getUserCourseSectionsByCourseId.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        if (result && result.sections && result.sections.length) {
          state.userCourseSections = result.sections;
        }
      })

      // ADMIN
      .addCase(cancelCoursZoomeSectionAsAdmin.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(cancelCourseTeamsSectionAsAdmin.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(rescheduleCoursZoomeSectionAsAdmin.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(rescheduleCourseTeamsSectionAsAdmin.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(updateCourseZoomSectionAsAdmin.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(updateCourseTeamsSectionAsAdmin.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(getSectionByIdAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload;

        state.adminSection = result;
      })
      .addCase(getAdminCourseSectionsByCourseId.fulfilled, (state, action) => {
        state.status = 'idle';

        if (
          action.payload &&
          action.payload.sections &&
          action.payload.sections.length
        ) {
          state.adminCourseSections = action.payload.sections;
        }
      })
      .addCase(createCourseHomeworkSectionAsUser.fulfilled, (state, action) => {
        state.status = 'idle';

        if (action.payload) {
          const { parentSection, section } = action.payload;

          if (parentSection) {
            state.newlyAddedSectionId = parentSection._id;

            const newSections = [
              ...(state.userCourseSections || []),
              parentSection,
            ];
            newSections.sort((a, b) => a.sortingKey - b.sortingKey);
            state.userCourseSections = newSections;
          } else if (section) {
            state.newlyAddedSectionId = section._id;
          }

          if (section && section.parent) {
            const newSections: Array<ISection> = JSON.parse(
              JSON.stringify([...state.userCourseSections])
            );

            const idToModify = section.parent;
            const dataToChange = section;

            const success = modifySectionListOnCreate(
              newSections,
              idToModify,
              dataToChange
            );

            if (success) {
              state.userCourseSections = newSections;
            }
          }
        }
      })
      .addCase(bookCourseMeeting.fulfilled, (state, action) => {
        state.status = 'idle';

        const result = action.payload || [];

        state.userCourseSections = [...result];
      });

    // Matchers
    builder
      .addMatcher(
        isPending(
          getUserCourseSectionsByCourseId,
          getAdminCourseSectionsByCourseId,
          deleteCourseSectionByIdAsAdmin,
          createNewCourseSectionAsAdmin,
          createNewCourseSectionAsAdminFromTemplate,
          createNewCourseZoomSectionAsAdminFromTemplate,
          createNewCourseTeamsSectionAsAdminFromTemplate,
          createNewCourseResourceSectionAsAdminForTemplate,
          createNewCourseZoomSectionAsAdmin,
          deleteCourseZoomSectionByIdAsAdmin,
          deleteCourseTeamSectionByIdAsAdmin,
          createNewCourseResourceSectionAsAdmin,
          deleteCourseResourceSectionByIdAsAdmin,
          updateCourseResourceSectionAsAdmin,
          createCourseHomeworkSectionAsAdmin,
          cancelCoursZoomeSectionAsAdmin,
          cancelCourseTeamsSectionAsAdmin,
          cancelCoursZoomeSectionAsUser,
          cancelCourseTeamsSectionAsUser,
          getSectionByIdAsAdmin,
          getSectionByIdAsUser,
          rescheduleCoursZoomeSectionAsUser,
          rescheduleCourseTeamsSectionAsUser,
          rescheduleCoursZoomeSectionAsAdmin,
          rescheduleCourseTeamsSectionAsAdmin,
          updateCourseHomeworkSectionAsAdmin,
          createCourseHomeworkSectionAsUser,
          updateCourseZoomSectionAsAdmin,
          updateCourseTeamsSectionAsAdmin,
          createNewCourseTeamSectionAsAdmin,
          bookCourseMeeting
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getUserCourseSectionsByCourseId,
          getAdminCourseSectionsByCourseId,
          deleteCourseSectionByIdAsAdmin,
          createNewCourseSectionAsAdmin,
          createNewCourseSectionAsAdminFromTemplate,
          createNewCourseZoomSectionAsAdminFromTemplate,
          createNewCourseTeamsSectionAsAdminFromTemplate,
          createNewCourseResourceSectionAsAdminForTemplate,
          createNewCourseZoomSectionAsAdmin,
          deleteCourseZoomSectionByIdAsAdmin,
          deleteCourseTeamSectionByIdAsAdmin,
          createNewCourseResourceSectionAsAdmin,
          deleteCourseResourceSectionByIdAsAdmin,
          updateCourseResourceSectionAsAdmin,
          createCourseHomeworkSectionAsAdmin,
          cancelCoursZoomeSectionAsAdmin,
          cancelCourseTeamsSectionAsAdmin,
          cancelCoursZoomeSectionAsUser,
          cancelCourseTeamsSectionAsUser,
          getSectionByIdAsAdmin,
          getSectionByIdAsUser,
          rescheduleCoursZoomeSectionAsUser,
          rescheduleCourseTeamsSectionAsUser,
          rescheduleCoursZoomeSectionAsAdmin,
          rescheduleCourseTeamsSectionAsAdmin,
          updateCourseHomeworkSectionAsAdmin,
          createCourseHomeworkSectionAsUser,
          updateCourseZoomSectionAsAdmin,
          updateCourseTeamsSectionAsAdmin,
          createNewCourseTeamSectionAsAdmin,
          bookCourseMeeting
        ),
        (state) => {
          state.status = 'rejected';
        }
      )
      // ADMIN
      // Create new course section
      .addMatcher(
        isAnyOf(
          createNewCourseSectionAsAdmin.fulfilled,
          createNewCourseSectionAsAdminFromTemplate.fulfilled,
          createNewCourseZoomSectionAsAdminFromTemplate.fulfilled,
          createNewCourseTeamsSectionAsAdminFromTemplate.fulfilled,
          createNewCourseResourceSectionAsAdminForTemplate.fulfilled,
          createNewCourseZoomSectionAsAdmin.fulfilled,
          createNewCourseResourceSectionAsAdmin.fulfilled,
          createCourseHomeworkSectionAsAdmin.fulfilled,
          createNewCourseTeamSectionAsAdmin.fulfilled
        ),
        (state, action) => {
          state.status = 'idle';

          if (action.payload && action.payload._id) {
            state.newlyAddedSectionId = action.payload._id;

            if (action.payload.parent) {
              const newSections: Array<ISection> = JSON.parse(
                JSON.stringify([...state.adminCourseSections])
              );

              const idToModify = action.payload.parent;
              const dataToChange = action.payload;

              const success = modifySectionListOnCreate(
                newSections,
                idToModify,
                dataToChange
              );

              if (success) {
                state.adminCourseSections = newSections;
              }
            } else {
              state.adminCourseSections = [
                ...(state.adminCourseSections || []),
                action.payload,
              ];
            }
          }
        }
      )
      // Update course sections
      .addMatcher(
        isAnyOf(
          updateCourseSectionAsAdminSavingSlice.fulfilled,
          updateCourseResourceSectionAsAdmin.fulfilled,
          updateCourseHomeworkSectionAsAdmin.fulfilled
        ),
        (state, action) => {
          state.status = 'idle';

          if (action.payload && action.payload._id) {
            const newSections: Array<ISection> = JSON.parse(
              JSON.stringify([...state.adminCourseSections])
            );

            const idToModify = action.payload._id;
            const dataToChange = action.payload;

            const success = modifySectionListOnUpdate(
              newSections,
              idToModify,
              dataToChange
            );

            if (success) {
              state.adminCourseSections = newSections;
            }
          }
        }
      )
      // Delete course zoom section
      .addMatcher(
        isAnyOf(
          deleteCourseSectionByIdAsAdmin.fulfilled,
          deleteCourseZoomSectionByIdAsAdmin.fulfilled,
          deleteCourseResourceSectionByIdAsAdmin.fulfilled,
          deleteCourseTeamSectionByIdAsAdmin.fulfilled
        ),
        (state, action) => {
          state.status = 'idle';

          if (action.payload && action.payload._id) {
            const newSections: Array<ISection> = JSON.parse(
              JSON.stringify([...state.adminCourseSections])
            );

            const idToModify = action.payload._id;

            const success = modifySectionListOnDeleteItem(
              newSections,
              idToModify
            );

            if (success) {
              state.adminCourseSections = newSections;
            }
          }
        }
      );
  },
});

export const {
  resetAdminCourseSections,
  resetNewlyAddedSectionId,
  resetUserCourseSections,
  updateAdminCourseSectionState,
  updateCourseSectionState,
  resetAdminSection,
  resetUserSection,
} = SectionsSlice.actions;

export const getSectionsState = (state: RootState): RootState['sections'] =>
  state.sections;

export * from './adminApi';
export * from './api';
export default SectionsSlice.reducer;
